import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { WelfareDetailDialog } from '@/components/spending/welfare-detail-dialog';
import { WelfareFormDialog } from '@/components/spending/welfare-form-dialog';
import {
  WELFARE_REQUEST_TYPES,
  WELFARE_STATUSES,
  type WelfareRequest,
  type WelfareRequestType,
} from '@/components/spending/welfare-types';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { FilterBar } from '@/components/shared/filter-bar';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { dataTableMoneyColumnTotals, type ListMoneyTotals } from '@/lib/money-totals';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate } from '@/utils/format';

const STATUS_ANY = '__any_status__';
const TYPE_ANY = '__any_type__';

const statusBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case 'disbursed':
      return 'success';
    case 'approved':
      return 'brand';
    case 'under_review':
      return 'warning';
    case 'rejected':
    case 'voided':
      return 'danger';
    default:
      return 'neutral';
  }
};

const WelfareStatusBadge = ({
  status,
  label,
}: {
  status: string;
  label: string;
}) => <Badge variant={statusBadgeVariant(status)}>{label}</Badge>;

export const WelfarePage = () => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);
  const [searchParams, setSearchParams] = useSearchParams();

  const canCreate = hasPermission(membership, PERMISSIONS.WELFARE_CREATE);
  const canReview = hasPermission(membership, PERMISSIONS.WELFARE_REVIEW);
  const canApprove = hasPermission(membership, PERMISSIONS.WELFARE_APPROVE);
  const canDisburse = hasPermission(membership, PERMISSIONS.WELFARE_DISBURSE);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_ANY);
  const [filterType, setFilterType] = useState(TYPE_ANY);
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WelfareRequest | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setFormOpen(true);
      setEditing(null);
    }
    const requestId = searchParams.get('request');
    if (requestId) setDetailId(requestId);
  }, [searchParams]);

  const list = useQuery({
    queryKey: [
      'welfare',
      'requests',
      page,
      pageSize,
      search,
      filterStatus,
      filterType,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: WelfareRequest[]; page: OffsetPage; totals?: ListMoneyTotals }>(
        '/welfare/requests',
        {
          query: {
            page,
            pageSize,
            q: search.trim() || undefined,
            status: filterStatus === STATUS_ANY ? undefined : filterStatus,
            requestType: filterType === TYPE_ANY ? undefined : filterType,
            dateFrom: filterDateRange.from ?? undefined,
            dateTo: filterDateRange.to ?? undefined,
          },
        },
      ),
    placeholderData: keepPreviousData,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['welfare'] });
  };

  const create = useMutation({
    mutationFn: (values: {
      memberId: string;
      requestType: WelfareRequestType;
      description: string;
      requestedAmountMinor: number;
      currency: string;
      confidentialNotes: string | null;
    }) => api.post<WelfareRequest>('/welfare/requests', values),
    onSuccess: async (row) => {
      toast.success(t('welfare.saved'));
      setFormOpen(false);
      setEditing(null);
      await invalidate();
      setDetailId(row.id);
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const update = useMutation({
    mutationFn: (values: {
      memberId: string;
      requestType: WelfareRequestType;
      description: string;
      requestedAmountMinor: number;
      currency: string;
      confidentialNotes: string | null;
      expectedUpdatedAt?: string;
    }) => {
      if (!editing) throw new Error('missing request');
      return api.patch<WelfareRequest>(`/welfare/requests/${editing.id}`, values);
    },
    onSuccess: async () => {
      toast.success(t('welfare.updated'));
      setFormOpen(false);
      setEditing(null);
      await invalidate();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const review = useMutation({
    mutationFn: () => api.post<WelfareRequest>(`/welfare/requests/${detailId}/review`, {}),
    onSuccess: async () => {
      toast.success(t('welfare.reviewed'));
      await invalidate();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const approve = useMutation({
    mutationFn: (values: { approvedAmountMinor: number; amountOverrideReason: string | null }) =>
      api.post<WelfareRequest>(`/welfare/requests/${detailId}/approve`, values),
    onSuccess: async () => {
      toast.success(t('welfare.approved'));
      await invalidate();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const reject = useMutation({
    mutationFn: () => api.post<WelfareRequest>(`/welfare/requests/${detailId}/reject`, {}),
    onSuccess: async () => {
      toast.success(t('welfare.rejected'));
      await invalidate();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const disburse = useMutation({
    mutationFn: (values: { financialPositionItemId: string; expenseDate: string }) =>
      api.post<WelfareRequest>(`/welfare/requests/${detailId}/disburse`, values, {
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: async () => {
      toast.success(t('welfare.disbursed'));
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['financial-position'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const voidDisbursement = useMutation({
    mutationFn: (reason: string) =>
      api.post<WelfareRequest>(`/welfare/requests/${detailId}/void`, { reason }),
    onSuccess: async () => {
      toast.success(t('welfare.voided'));
      await invalidate();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterStatus !== STATUS_ANY) count += 1;
    if (filterType !== TYPE_ANY) count += 1;
    if (filterDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [filterDateRange.preset, filterStatus, filterType, search]);

  const columns: DataTableColumn<WelfareRequest>[] = [
    {
      id: 'createdAt',
      header: t('welfare.columns.date'),
      cell: (row) => formatDate(row.createdAt, { locale, style: 'medium' }),
    },
    {
      id: 'member',
      header: t('welfare.columns.member'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    {
      id: 'type',
      header: t('welfare.columns.type'),
      cell: (row) => t(`welfare.types.${row.requestType}`, { defaultValue: row.requestType }),
    },
    {
      id: 'amount',
      header: t('welfare.columns.amount'),
      money: (row) => ({ amount: row.requestedAmountMinor, currency: row.currency }),
    },
    {
      id: 'status',
      header: t('welfare.columns.status'),
      cell: (row) => (
        <WelfareStatusBadge status={row.status} label={t(`welfare.status.${row.status}`)} />
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  const clearCreateParam = () => {
    if (searchParams.has('create') || searchParams.has('request')) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      next.delete('request');
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <div>
      <PageHeader
        title={t('welfare.title')}
        description={t('welfare.description')}
        headerActions={
          canCreate ? (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              {t('welfare.create')}
            </Button>
          ) : null
        }
      />

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterStatus(STATUS_ANY);
          setFilterType(TYPE_ANY);
          setFilterDateRange({ preset: 'allTime', ...resolvePreset('allTime', timezone) });
          setPage(1);
        }}
        labels={{
          filters: t('welfare.filters.title'),
          clearAll: t('welfare.filters.clearAll'),
          applied: (count) => t('welfare.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('welfare.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('welfare.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value ?? STATUS_ANY);
                  setPage(1);
                }}
                options={[
                  { value: STATUS_ANY, label: t('welfare.filters.statusAny') },
                  ...WELFARE_STATUSES.map((status) => ({
                    value: status,
                    label: t(`welfare.status.${status}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterType}
                onChange={(value) => {
                  setFilterType(value ?? TYPE_ANY);
                  setPage(1);
                }}
                options={[
                  { value: TYPE_ANY, label: t('welfare.filters.typeAny') },
                  ...WELFARE_REQUEST_TYPES.map((value) => ({
                    value,
                    label: t(`welfare.types.${value}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('welfare.filters.dateFrom'),
                to: t('welfare.filters.dateTo'),
              }}
            />
          </>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetailId(row.id)}
        totals={dataTableMoneyColumnTotals(
          'amount',
          list.data?.totals,
          church?.baseCurrency ?? 'GHS',
        )}
        empty={
          <p className="py-12 text-center text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('welfare.emptyFiltered') : t('welfare.empty')}
          </p>
        }
      />

      {pageMeta ? (
        <Pagination
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <WelfareFormDialog
        open={formOpen}
        onOpenChange={(next) => {
          setFormOpen(next);
          if (!next) {
            setEditing(null);
            clearCreateParam();
          }
        }}
        editing={editing}
        submitting={create.isPending || update.isPending}
        onSubmit={(values) => {
          if (editing) update.mutate(values);
          else create.mutate(values);
        }}
      />

      <WelfareDetailDialog
        requestId={detailId}
        open={Boolean(detailId)}
        onOpenChange={(next) => {
          if (!next) {
            setDetailId(null);
            clearCreateParam();
          }
        }}
        canCreate={canCreate}
        canReview={canReview}
        canApprove={canApprove}
        canDisburse={canDisburse}
        onEdit={(row) => {
          setEditing(row);
          setFormOpen(true);
        }}
        onReview={() => review.mutateAsync().then(() => undefined)}
        onApprove={(values) => approve.mutate(values)}
        onReject={() => reject.mutateAsync().then(() => undefined)}
        onDisburse={(values) => disburse.mutate(values)}
        onVoid={(reason) => voidDisbursement.mutate(reason)}
        reviewing={review.isPending}
        approving={approve.isPending}
        rejecting={reject.isPending}
        disbursing={disburse.isPending}
        voiding={voidDisbursement.isPending}
      />
    </div>
  );
};
